import { getCookie, setCookie } from './cookies';

export const ANALYTICS_CONSENT_EVENT = 'astralbeam:analytics-consent';
export const CONSENT_KEY = 'astralbeam.analytics-consent';
let sessionChoice: AnalyticsConsent | undefined;
export type AnalyticsConsent = 'granted' | 'denied';
let measurementId = '';
let trackerFrame: HTMLIFrameElement | undefined;
let lastPath: string | undefined;

// Explicit choices do not expire: in particular an opt-out must not silently
// turn back on. Storage clearing resets to the product's default-on policy.
function storedChoice(): AnalyticsConsent | null {
  let local: string | null = null;
  let cookie: string | undefined;
  let readable = false;
  try { local = localStorage.getItem(CONSENT_KEY); readable = true; } catch { /* Try cookie fallback. */ }
  try { cookie = getCookie('consent'); readable = true; } catch { /* Fail closed if both are blocked. */ }
  if (local === 'denied' || cookie === 'denied' || !readable) return 'denied';
  return local === 'granted' || cookie === 'granted' ? 'granted' : null;
}

function hasSavedChoice(choice: AnalyticsConsent): boolean {
  let saved = false;
  try { saved = localStorage.getItem(CONSENT_KEY) === choice; } catch { /* Cookie fallback. */ }
  try { saved ||= getCookie('consent') === choice; } catch { /* Storage fallback. */ }

  return saved && storedChoice() === choice;
}

export function getAnalyticsConsent(): AnalyticsConsent | null {
  return sessionChoice ?? storedChoice();
}

function applyChoice(consent: AnalyticsConsent | null) {
  const id = measurementId || config.google_analytics_tag;
  const denied = consent === 'denied';
  const tracker = trackerFrame?.contentWindow;
  const validId = /^G-[A-Z0-9]+$/.test(id);
  if (validId) {
    (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = denied;
    if (tracker) (tracker as unknown as Record<string, unknown>)[`ga-disable-${id}`] = denied;
  }
  if (!denied) return;
  lastPath = undefined;
  // Unload only the SDK's browsing context, never the bridge document. Removing
  // its frame cancels SDK timers/listeners and pending batches without destroying
  // wallet signing callbacks or a submitted transaction's in-memory recovery hash.
  if (window.dataLayer) window.dataLayer.length = 0;
  window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
  trackerFrame?.remove();
  trackerFrame = undefined;
  measurementId = '';
  delete window.gtag;
  try {
    // The pre-telemetry loader used G-S3F18E23RH and GA's auto domain.
    // Match both cookie scopes, including when this runtime has no active ID.
    const names = new Set(['_ga', '_ga_S3F18E23RH', ...(validId ? [`_ga_${id.slice(2)}`] : [])]);
    for (const name of names) {
      setCookie(name, '', { 'max-age': 0, path: '/' });
      if (location.hostname === 'astralbeam.io' || location.hostname.endsWith('.astralbeam.io')) {
        setCookie(name, '', { 'max-age': 0, path: '/', domain: 'astralbeam.io' });
      }
    }
  } catch { /* Disabled destination remains the immediate safety gate. */ }
}

export function syncAnalyticsConsent() {
  applyChoice(getAnalyticsConsent());
  window.dispatchEvent(new Event(ANALYTICS_CONSENT_EVENT));
}

export function setAnalyticsConsent(consent: AnalyticsConsent): boolean {
  sessionChoice = consent;
  try { localStorage.setItem(CONSENT_KEY, consent); } catch { /* Cookie fallback. */ }
  try { setCookie('consent', consent, { 'max-age': 34560000, secure: location.protocol === 'https:' }); } catch { /* Session choice still applies. */ }
  const saved = hasSavedChoice(consent);
  // A failed write must not reload into a stale saved grant or enable over a
  // stale opt-out. Keep denial in this document and expose persistence failure.
  if (!saved) sessionChoice = 'denied';
  else sessionChoice = undefined;
  lastPath = undefined;
  if (measurementId && consent === 'granted' && saved) {
    (window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = false;
    window.gtag?.('consent', 'update', { analytics_storage: 'granted' });
  }
  syncAnalyticsConsent();

  return saved;
}

// Never pass raw URLs, query/hash, transaction IDs, wallet data or document titles to GA.
export function analyticsPath(path: string): string {
  const pathname = path.split(/[?#]/)[0];
  if (/^\/transactions\/[^/]+\/?$/.test(pathname)) return '/transactions/:id';
  return ['/', '/transactions', '/how-it-works', '/faucet', '/privacy-policy', '/terms-of-service', '/status'].includes(pathname)
    ? pathname : '/other';
}

export function trackPageView(path: string, id = config.google_analytics_tag) {
  if (getAnalyticsConsent() === 'denied') return;
  if (!/^G-[A-Z0-9]+$/.test(id)) return;
  const safePath = analyticsPath(path);
  const page = {
    page_path: safePath,
    page_location: window.location.origin + safePath,
    page_referrer: '',
    page_title: 'AstralBeam',
  };
  if (!measurementId) {
    // SDK code runs in its own same-origin document, never the wallet window.
    // A real same-origin URL (not about:blank) is needed for GA cookie handling.
    const frame = document.createElement('iframe');
    trackerFrame = frame;
    frame.hidden = true;
    frame.title = 'Analytics';
    frame.setAttribute('aria-hidden', 'true');
    frame.referrerPolicy = 'no-referrer';
    frame.src = `${import.meta.env.BASE_URL}analytics.html`;
    measurementId = id;
    (window as unknown as Record<string, unknown>)[`ga-disable-${id}`] = false;
    window.dataLayer = [];
    // gtag's queue protocol uses Arguments objects, not rest-parameter arrays.
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function () { window.dataLayer!.push(arguments); };
    window.gtag('consent', 'default', {
      analytics_storage: 'granted', ad_storage: 'denied',
      ad_user_data: 'denied', ad_personalization: 'denied',
    });
    window.gtag('set', 'linker', { domains: [], accept_incoming: false, decorate_forms: false });
    window.gtag('js', new Date());
    window.gtag('config', id, {
      ...page, send_page_view: false, allow_google_signals: false,
      allow_ad_personalization_signals: false, cookie_domain: 'none',
      cookie_path: '/', cookie_flags: 'SameSite=Lax;Secure',
    });
    frame.onload = () => {
      // A delayed load must not resurrect a removed/denied tracker.
      if (trackerFrame !== frame) return;
      if (getAnalyticsConsent() === 'denied') {
        applyChoice('denied');

        return;
      }
      const tracker = frame.contentWindow;
      if (!tracker) return;
      tracker.dataLayer = window.dataLayer;
      tracker.gtag = window.gtag;
      const script = tracker.document.createElement('script');
      script.async = true;
      script.referrerPolicy = 'no-referrer';
      script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
      tracker.document.head.appendChild(script);
    };
    document.body.appendChild(frame);
  }
  // Raw pathname is used only for local deduplication, never in SDK arguments.
  const pathname = path.split(/[?#]/)[0];
  if (lastPath === pathname) return;
  lastPath = pathname;
  window.gtag?.('set', page);
  window.gtag?.('event', 'page_view', { ...page, send_to: measurementId });
}
